/**
 * Freeform section factory and block manipulation. Creates freeform sections
 * that satisfy the render/export/script pipeline contracts while supporting
 * arbitrary block insertion.
 *
 * Freeform Section Contract:
 *   instanceId:  generateInstanceId()
 *   catalogKey:  'freeform'
 *   sectionId:   'freeform-' + shortId
 *   category:    'freeform'
 *   type:        'freeform'
 *   html:        Single <section> root element
 *   css:         '' (empty — blocks use inline styles or global CS classes)
 *   js:          '' (empty)
 *   overrides:   null
 */

import { stampEditableIds, BLOCK_ROOT_ATTR } from '../editing/editable-utils.js';
import { getBlockTemplate } from '../editing/block-templates.js';

function shortId() {
  return crypto.randomUUID().slice(0, 8);
}

function generateInstanceId() {
  return 'inst-' + crypto.randomUUID().slice(0, 8);
}

/**
 * Build the default HTML for a new freeform section (empty with placeholder).
 * @param {string} sectionId
 * @returns {string}
 */
function buildFreeformHTML(sectionId) {
  return `<section id="${sectionId}">
  <div class="cs-container" style="max-width:50rem;margin:0 auto;padding:var(--sectionPadding, clamp(3.75rem, 7.82vw, 6.25rem)) 1rem">
    <p data-pb-editable="text" style="text-align:center;color:#94a3b8;font-size:1.125rem">Click the + button below to add your first block</p>
  </div>
</section>`;
}

/**
 * Create a freeform section data object satisfying all pipeline contracts.
 * @returns {{ instanceId, catalogKey, sectionId, category, type, html, css, js, overrides }}
 */
export function createFreeformSection() {
  const sid = 'freeform-' + shortId();
  const rawHTML = buildFreeformHTML(sid);
  const { html } = stampEditableIds(rawHTML);

  return {
    instanceId: generateInstanceId(),
    catalogKey: 'freeform',
    sectionId: sid,
    category: 'freeform',
    type: 'freeform',
    html,
    css: '',
    js: '',
    overrides: null
  };
}

/**
 * Ensure EVERY direct block root in a freeform section carries a persisted
 * identity (UX Pack F4a). Pass 1 lets the standard classifier mint eids for
 * everything it recognizes; pass 2 marks the remainder (dividers, embeds —
 * anything nothing classifies) with BLOCK_ROOT_ATTR, which injectEditableAttrs
 * turns into a container identity on every render, so the re-stamp mints a
 * ROOT eid. Without this, sibling discovery falls back to the first
 * DESCENDANT eid and an override written to it styles the descendant, not
 * the block. Idempotent: marked roots and stamped eids are untouched.
 * @param {string} sectionHTML
 * @returns {{ html: string, changed: boolean }}
 */
export function stampFreeformBlockRoots(sectionHTML, options = {}) {
  if (typeof DOMParser === 'undefined') return { html: sectionHTML, changed: false };
  // Pass 1: let the standard classifier mint eids for everything it already
  // recognizes (authored editables, classified containers).
  const first = stampEditableIds(sectionHTML, options);
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${first.html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const sectionEl = root?.firstElementChild;
  if (!sectionEl) return { html: first.html, changed: first.stamped };
  const container = sectionEl.querySelector('.cs-container') || sectionEl;
  // Pass 2: any direct block root STILL without an identity gets the
  // persisted marker, which injectEditableAttrs classifies as a container on
  // every render — so the re-stamp mints it a root eid.
  let marked = false;
  for (const child of Array.from(container.children)) {
    if (!child.getAttribute('data-pb-eid') && !child.hasAttribute(BLOCK_ROOT_ATTR)) {
      child.setAttribute(BLOCK_ROOT_ATTR, '');
      marked = true;
    }
  }
  if (!marked) return { html: first.html, changed: first.stamped };
  const second = stampEditableIds(root.innerHTML, options);
  return { html: second.html, changed: true };
}

/**
 * Insert a block into a freeform section's HTML at the given position.
 * @param {string} sectionHTML - The current section HTML
 * @param {string} blockKey - Block type key from block-templates.js
 * @param {number} [position] - Insert index among existing blocks (defaults to end)
 * @returns {{ html: string, success: boolean }}
 */
export function insertBlock(sectionHTML, blockKey, position, options = {}) {
  const template = getBlockTemplate(blockKey);
  if (!template) return { html: sectionHTML, success: false };

  const blockHTML = template.create();

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHTML}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const sectionEl = root.firstElementChild;
  if (!sectionEl) return { html: sectionHTML, success: false };

  // Find the inner container (first child div or the section itself)
  const container = sectionEl.querySelector('.cs-container') || sectionEl;

  // Parse block HTML
  const blockDoc = parser.parseFromString(`<div>${blockHTML}</div>`, 'text/html');
  const blockRoot = blockDoc.body.firstElementChild;
  const blockNodes = Array.from(blockRoot.childNodes);

  // Remove placeholder text if present (the initial "Click the + button" paragraph)
  const placeholder = container.querySelector(
    'p[style*="text-align:center"][style*="color:#94a3b8"]'
  );
  if (placeholder) placeholder.remove();

  // Insert at position or append
  const children = Array.from(container.children);
  const insertBefore =
    typeof position === 'number' && position < children.length ? children[position] : null;

  for (const node of blockNodes) {
    if (insertBefore) {
      container.insertBefore(node, insertBefore);
    } else {
      container.appendChild(node);
    }
  }

  // Mark eid-less block roots + re-stamp editable IDs on new elements
  const { html } = stampFreeformBlockRoots(root.innerHTML, options);
  return { html, success: true };
}

/**
 * Remove a block from a freeform section's HTML by its editable ID.
 * @param {string} sectionHTML - The current section HTML
 * @param {string} eid - The data-pb-eid of the element to remove
 * @returns {{ html: string, success: boolean }}
 */
export function removeBlock(sectionHTML, eid) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHTML}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  const target = root.querySelector(`[data-pb-eid="${eid}"]`);
  if (!target) return { html: sectionHTML, success: false };

  // Remove the block element (walk up to the direct child of the container)
  const sectionEl = root.firstElementChild;
  const container = sectionEl?.querySelector('.cs-container') || sectionEl;
  let toRemove = target;

  // Walk up to the container's direct child level
  while (toRemove.parentElement && toRemove.parentElement !== container) {
    toRemove = toRemove.parentElement;
  }
  toRemove.remove();

  return { html: root.innerHTML, success: true };
}

/**
 * Reorder a block within a freeform section.
 * @param {string} sectionHTML - The current section HTML
 * @param {number} fromIndex - Current block index
 * @param {number} toIndex - Target block index
 * @returns {{ html: string, success: boolean }}
 */
export function reorderBlock(sectionHTML, fromIndex, toIndex) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHTML}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const sectionEl = root.firstElementChild;
  const container = sectionEl?.querySelector('.cs-container') || sectionEl;
  if (!container) return { html: sectionHTML, success: false };

  const children = Array.from(container.children);
  if (fromIndex < 0 || fromIndex >= children.length) return { html: sectionHTML, success: false };
  if (toIndex < 0 || toIndex >= children.length) return { html: sectionHTML, success: false };
  if (fromIndex === toIndex) return { html: sectionHTML, success: true };

  const moving = children[fromIndex];
  moving.remove();

  const updatedChildren = Array.from(container.children);
  if (toIndex >= updatedChildren.length) {
    container.appendChild(moving);
  } else {
    container.insertBefore(moving, updatedChildren[toIndex]);
  }

  return { html: root.innerHTML, success: true };
}
